import React from 'react';
import styled from 'styled-components';
import { commonStyles } from '../../theme';

const BoardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  max-width: 100%;
  overflow-x: auto;
`;

const GameGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px;
  background-color: ${commonStyles.boardBackground};
  border-radius: 8px;
  
  @media (max-width: 768px) {
    gap: 2px;
    width: 100%;
    padding: 0;
  }
`;

const WordRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  
  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const LetterBox = styled.div`
  width: 50px;
  height: 50px;
  border: 2px solid ${commonStyles.tileBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => 
    props.state === 'correct' || props.state === 'present' 
      ? 'white' 
      : commonStyles.tileText
  };
  visibility: ${props =>
    props.state === 'blank' ? 'hidden' : 'visible'
  };
  background-color: ${props => 
    props.state === 'correct' 
      ? commonStyles.tileCorrect
      : props.state === 'present'
        ? commonStyles.tileCorrect
        : props.state === 'absent'
          ? commonStyles.tileAbsent
          : props.state === 'guessing'
            ? 'var(--bg-tertiary)'
            : props.filled
              ? commonStyles.tileFilled
              : commonStyles.tileEmpty
  };
  border-color: ${props => 
    props.state === 'correct' 
      ? commonStyles.tileCorrect
      : props.state === 'present'
        ? commonStyles.tileCorrect
        : props.state === 'absent'
          ? commonStyles.tileAbsent
          : props.state === 'guessing'
            ? commonStyles.keyGuessBorder
            : props.filled
              ? commonStyles.tileFilled
              : commonStyles.tileBorder
  };
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const WordInfo = styled.div`
  margin-top: 4px;
  font-size: 0.7rem;
  color: var(--color-word-info, #818384);
  text-align: center;
`;

const IncorrectGuesses = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-error-text, #f87171);
  margin-top: 6px;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-top: 2px;
  }
`;

const GameBoard = ({ 
  puzzleState,
  activeIndex = -1,
  showWordLength = false,
  showFirstLetter = false,
  currentGuess = ''
}) => {
  if (!puzzleState || !puzzleState.words) {
    return (
      <BoardContainer>
        <GameGrid>
          <div style={{ color: 'white', textAlign: 'center' }}>No puzzle data available</div>
        </GameGrid>
      </BoardContainer>
    );
  }

  const renderWordRow = (word, wordIndex, tileCount) => {
    const isActive = wordIndex === activeIndex;
    const wordLength = word.word_length || 12;
    const letters = [];
    
    // Create individual letter boxes for the actual word length only
    for (let i = 0; i < tileCount; i++) {
      let letter = '';
      let state = 'empty';
      let filled = false;
      
      if (i >= wordLength) {
        letter = null;
        state = 'blank';
        filled = true;
      } else if (word.complete && word.word && i < word.word.length) {
        // Show completed word letters
        letter = word.word[i];
        state = 'correct';
        filled = true;
      } else if (!word.complete && word.word && i < word.word.length) {
        // Show partial word based on settings
        if (showFirstLetter && i === 0) {
          letter = word.word[i];
          filled = true;
        } else if (word.word[i] !== '?') {
          letter = word.word[i];
          filled = true;
        }
      }
      
      // If this is the active word, show current guess letters after the existing word
      if (isActive && !word.complete && currentGuess) {
        const baseWordLength = word.word ? word.word.length : 0;
        const guessIndex = i - baseWordLength;
        
        if (guessIndex >= 0 && guessIndex < currentGuess.length) {
          letter = currentGuess[guessIndex];
          filled = true;
          state = 'guessing';
        }
      }
      
      letters.push(
        <LetterBox
          key={i}
          state={state}
          filled={filled}
          role="img"
          aria-label={`Position ${i + 1}: ${letter || 'empty'}`}
        >
          {letter}
        </LetterBox>
      );
    }
    
    return (
      <div key={wordIndex}>
        <WordRow 
          role="group"
          aria-label={`Word ${wordIndex + 1}: ${word.complete ? 'completed' : isActive ? 'active' : 'pending'}`}
        >
          {letters}
        </WordRow>
        {word.incorrect_guesses && word.incorrect_guesses.length > 0 && (
          <WordInfo>
            <IncorrectGuesses>
              {word.incorrect_guesses.join(', ')}
            </IncorrectGuesses>
          </WordInfo>
        )}
      </div>
    );
  };

  const maxLength = Math.max(...puzzleState.words.map(word => word.word_length || 0));

  return (
    <BoardContainer>
      <GameGrid role="group" aria-label="Puzzle words">
        {puzzleState.words.map((word, index) => renderWordRow(word, index, showWordLength ? maxLength : 12))}
      </GameGrid>
    </BoardContainer>
  );
};

export default GameBoard;
